/**
 * Every code the catalog import answers with, from bos-api
 * `Models/CatalogImports/CatalogImportCodes.cs` (API-185, extended by API-195),
 * with the kind the server gives it there:
 *
 * - `header`: belongs to no row: a refusal of the whole request, a problem in
 *   the result's `header`, or an undo refusal.
 * - `problem`: on a row (or a names entry); the row needs attention and the
 *   import refuses until it is fixed or excluded, unless the request sends
 *   `importDrafts` and the problem has a fallback: the row is then `draft`.
 * - `warning`: on a row; what the import will do differently. Never blocks.
 *
 * 53 constants of that file plus `BRANCH_NOT_ALLOWED`, the branch-scope
 * guard's own code, which its `Kinds` lists because the import answers with
 * it too: 54 codes. `catalogImportCodes.test.ts` checks this list against a
 * checked-in copy of the server file, so re-copy the file when the server
 * adds a code and the test names what is missing.
 *
 * The notice sentences key on `CatalogImportCode`, so a missing sentence is a
 * type error rather than a raw code on screen.
 */
export const CATALOG_IMPORT_CODE_KINDS = {
    IMPORT_TOO_MANY_LINES: 'header',
    IMPORT_TOO_MANY_UNITS: 'header',
    IMPORT_LINES_INVALID: 'header',
    IMPORT_REQUEST_INVALID: 'header',
    BRANCH_UNKNOWN: 'header',
    BRANCH_ARCHIVED: 'header',
    BRANCH_NOT_ALLOWED: 'header',
    ACCOUNTING_NOT_READY: 'header',
    IMPORT_NOT_FOUND: 'header',
    IMPORT_CONFLICT: 'header',
    ONBOARDING_FINISHED: 'header',
    IMPORT_NOT_LATEST: 'header',
    IMPORT_ALREADY_UNDONE: 'header',
    IMPORT_CONSUMED: 'header',
    AXES_DIFFER_FROM_PRODUCT: 'problem',
    PRODUCT_IS_SIMPLE: 'problem',
    BARCODE_AMBIGUOUS: 'problem',
    SKU_BARCODE_CONFLICT: 'problem',
    BARCODE_LOOKS_LIKE_IMEI: 'problem',
    VARIANT_UNKNOWN: 'problem',
    GROUP_FIELDS_DIFFER: 'problem',
    GROUP_AXES_DIFFER: 'problem',
    BARCODE_SHARED_ACROSS_SKUS: 'problem',
    VARIANT_FIELDS_DIFFER: 'problem',
    AXIS_NOT_ON_CATEGORY: 'problem',
    CATEGORY_REQUIRED_FOR_OPTIONS: 'problem',
    ATTRIBUTE_TYPE_UNKNOWN: 'problem',
    ATTRIBUTE_VALUE_UNKNOWN: 'problem',
    SKU_REQUIRED: 'problem',
    NAME_REQUIRED: 'problem',
    SALE_PRICE_REQUIRED: 'problem',
    VALUE_TOO_LONG: 'problem',
    QUANTITY_REQUIRED: 'problem',
    QUANTITY_NOT_WHOLE: 'problem',
    COST_REQUIRED: 'problem',
    AMOUNT_NEGATIVE: 'problem',
    VAT_TYPE_UNKNOWN: 'problem',
    UNIT_UNKNOWN: 'problem',
    BRAND_AMBIGUOUS: 'problem',
    CATEGORY_AMBIGUOUS: 'problem',
    SUPPLIER_AMBIGUOUS: 'problem',
    NAME_TARGET_UNKNOWN: 'problem',
    NAME_MERGE_CYCLE: 'problem',
    AXIS_VALUE_UNSPECIFIED: 'warning',
    BARCODE_FROM_SKU: 'warning',
    VALUE_WILL_BE_CREATED: 'warning',
    SALE_BELOW_COST: 'warning',
    DATE_CAPPED_TO_TODAY: 'warning',
    CATALOGUE_KEPT: 'warning',
    PRICE_DIFFERS_FROM_CATALOGUE: 'warning',
    /** A warning since API-195: `params { field, value, used }`. */
    DATE_INVALID: 'warning',
    BARCODES_KEPT_AS_ALTERNATES: 'warning',
    BARCODE_ADDED_AS_ALTERNATE: 'warning',
    PRICE_FROM_PRIMARY_BARCODE: 'warning',
} as const;

export type CatalogImportCode = keyof typeof CATALOG_IMPORT_CODE_KINDS;

export type CatalogImportCodeKind =
    (typeof CATALOG_IMPORT_CODE_KINDS)[CatalogImportCode];

export const CATALOG_IMPORT_CODES = Object.keys(
    CATALOG_IMPORT_CODE_KINDS,
) as CatalogImportCode[];

export const isCatalogImportCode = (raw: unknown): raw is CatalogImportCode =>
    typeof raw === 'string' && Object.hasOwn(CATALOG_IMPORT_CODE_KINDS, raw);

/**
 * Why an undo kept a name the import created, from bos-api
 * `CatalogImportKeptReasons` (`Models/CatalogImports/CatalogImportUndoModels.cs`).
 * Stable strings the app localises; the same test pins them against a
 * checked-in copy of that file.
 */
export const CATALOG_IMPORT_KEPT_REASONS = [
    /** A product outside the import uses it (brand, category, or an axis it varies by). */
    'USED_BY_PRODUCTS',
    /** A category outside the import sits under it. */
    'HAS_SUBCATEGORIES',
    /** Stock outside the import names it (a supplier's batches). */
    'USED_BY_STOCK',
    /** A purchase invoice or one of its lines names it. */
    'USED_BY_PURCHASES',
    /** A supplier payment names it. */
    'USED_BY_PAYMENTS',
    /** A variant outside the import takes the value. */
    'USED_BY_VARIANTS',
] as const;

export type CatalogImportKeptReason =
    (typeof CATALOG_IMPORT_KEPT_REASONS)[number];

export const isCatalogImportKeptReason = (
    raw: unknown,
): raw is CatalogImportKeptReason =>
    typeof raw === 'string' &&
    (CATALOG_IMPORT_KEPT_REASONS as readonly string[]).includes(raw);
